import React, { useEffect, useId, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { submitEnquiry } from "../../services/enquiryService";
import { validatePhoneNumber } from "../utils/phoneValidation";
import "./EnquiryModal.css";

const initialForm = () => ({
  name: "",
  email: "",
  phoneNumber: "",
  companyName: "",
});

const EnquiryModal = ({ open, onClose }) => {
  const titleId = useId();
  const successTitleId = useId();
  const [step, setStep] = useState("form");
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setStep("form");
      setForm(initialForm());
      setError("");
      setPhoneError("");
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setError("");
    if (field === "phoneNumber") {
      setPhoneError("");
    }
  };

  const handlePhoneBlur = () => {
    if (!form.phoneNumber.trim()) {
      setPhoneError("");
      return;
    }
    setPhoneError(validatePhoneNumber(form.phoneNumber) || "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const phoneNumber = form.phoneNumber.trim();
    const companyName = form.companyName.trim();

    const phoneMsg = validatePhoneNumber(form.phoneNumber);
    setPhoneError(phoneMsg || "");
    if (phoneMsg) {
      setError("");
      return;
    }

    if (!name || !email || !phoneNumber || !companyName) {
      setError("Please fill in all required fields.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const res = await submitEnquiry({ name, email, phoneNumber, companyName });
      setStep("success");
      const thankYou = res?.message || "Thank you — we will be in touch.";
      toast.success(thankYou, { autoClose: 5000 });
    } catch (err) {
      const data = err.response?.data;
      let msg =
        data?.message ||
        (typeof data === "string" ? data : null) ||
        data?.title ||
        "Something went wrong. Please try again.";
      if (data?.errors && typeof data.errors === "object") {
        const first = Object.values(data.errors).flat()[0];
        if (typeof first === "string") msg = first;
      }
      setError(typeof msg === "string" ? msg : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "success") {
    return (
      <div className="max-enquiry-overlay" role="presentation" onClick={onClose}>
        <div
          className="max-enquiry-dialog max-enquiry-dialog--success"
          role="dialog"
          aria-modal="true"
          aria-labelledby={successTitleId}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-enquiry-dialog__head">
            <h2 id={successTitleId} className="max-enquiry-dialog__title">
              Thank you
            </h2>
            <button type="button" className="max-enquiry-dialog__close" onClick={onClose} aria-label="Close">
              <X size={20} strokeWidth={1.75} />
            </button>
          </div>
          <p className="max-enquiry-success__text">
            We received your details and will be in touch shortly.
          </p>
          <div className="max-enquiry-form__actions max-enquiry-form__actions--single">
            <button type="button" className="max-btn max-btn--solid" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-enquiry-overlay" role="presentation" onClick={onClose}>
      <div
        className="max-enquiry-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-enquiry-dialog__head">
          <h2 id={titleId} className="max-enquiry-dialog__title">
            Reach the MaX team
          </h2>
          <button type="button" className="max-enquiry-dialog__close" onClick={onClose} aria-label="Close">
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>
        <p className="max-enquiry-dialog__intro">
          Leave your details and we will get back to you shortly.
        </p>
        <form className="max-enquiry-form" onSubmit={handleSubmit} noValidate>
          <label className="max-enquiry-field">
            <span className="max-enquiry-field__label">Name</span>
            <input
              type="text"
              placeholder="Enter Your Name"
              name="name"
              autoComplete="name"
              value={form.name}
              onChange={handleChange("name")}
              className="max-enquiry-field__input"
              disabled={submitting}
              required
            />
          </label>
          <label className="max-enquiry-field">
            <span className="max-enquiry-field__label">Email Address</span>
            <input
              type="email"
              placeholder="Enter Your Email Address"
              name="email"
              autoComplete="email"
              value={form.email}
              onChange={handleChange("email")}
              className="max-enquiry-field__input"
              disabled={submitting}
              required
            />
          </label>
          <label className="max-enquiry-field">
            <span className="max-enquiry-field__label">Phone number</span>
            <input
              type="tel"
              name="phoneNumber"
              autoComplete="tel"
              inputMode="tel"
              value={form.phoneNumber}
              onChange={handleChange("phoneNumber")}
              onBlur={handlePhoneBlur}
              className={`max-enquiry-field__input ${phoneError ? "max-enquiry-field__input--invalid" : ""}`}
              disabled={submitting}
              aria-invalid={Boolean(phoneError)}
              aria-describedby={phoneError ? "phone-error" : undefined}
              placeholder="e.g. +91 98765 43210"
            />
            {phoneError ? (
              <span id="phone-error" className="max-enquiry-field__hint max-enquiry-field__hint--error" role="alert">
                {phoneError}
              </span>
            ) : (
              <span className="max-enquiry-field__hint">8–15 digits (country code allowed)</span>
            )}
          </label>
          <label className="max-enquiry-field">
            <span className="max-enquiry-field__label">Company name</span>
            <input
              type="text"
              placeholder="Enter Your Company Name"
              name="companyName"
              autoComplete="organization"
              value={form.companyName}
              onChange={handleChange("companyName")}
              className="max-enquiry-field__input"
              disabled={submitting}
              required
            />
          </label>
          {error ? <p className="max-enquiry-form__error">{error}</p> : null}
          <div className="max-enquiry-form__actions">
            <button type="button" className="max-btn max-btn--outline" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="max-btn max-btn--solid" disabled={submitting}>
              {submitting ? "Sending…" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnquiryModal;
